import { useCallback, useEffect, useState } from "react";
import {
  ToolContainer,
  FileDropZone,
  DownloadButton,
  downloadBlob,
} from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { placeInCell } from "./collageFit";
import { decodeImage, canvasToBlob } from "./decode";
import { ArrowUp, ArrowDown, X } from "lucide-react";
import { NoticeBox } from "@/components/NoticeBox";

type Cell = [number, number, number, number]; // x, y, w, h in grid units
const grid = (cols: number, rows: number, n = cols * rows): { cols: number; rows: number; cells: Cell[] } => ({
  cols, rows,
  cells: Array.from({ length: n }, (_, i) => [i % cols, Math.floor(i / cols), 1, 1] as Cell),
});
const LAYOUTS: Record<string, { cols: number; rows: number; cells: Cell[] }> = {
  "2 (side by side)": grid(2, 1),
  "2 (stacked)": grid(1, 2),
  "3 (row)": grid(3, 1),
  "3 (1 big + 2)": { cols: 2, rows: 2, cells: [[0, 0, 2, 1], [0, 1, 1, 1], [1, 1, 1, 1]] },
  "4 (grid)": grid(2, 2),
  "5 (2 + 3)": { cols: 6, rows: 2, cells: [[0, 0, 3, 1], [3, 0, 3, 1], [0, 1, 2, 1], [2, 1, 2, 1], [4, 1, 2, 1]] },
  "6 (grid)": grid(3, 2),
  "9 (grid)": grid(3, 3),
};

const TOOL_INFO_CONTENT = {
  what: `Combines 2 to 9 photos into a single collage image, using a choice of grid layouts, for sharing multiple photos as one image instead of several separate ones — useful for a social media post that only accepts one image, a quick visual summary of an event, or just avoiding sending a dozen separate attachments when a few would do.`,
  how: `Upload your photos, choose a layout (side-by-side, a grid, and so on), adjust the spacing between photos if you like, and tick Fit (no crop) to keep each whole photo visible (the spare space in a cell shows the background colour) instead of the default fill, which crops photos to cover their cell, and generate and download the finished collage.`,
  tip: `Photos with roughly similar aspect ratios (all portrait, or all landscape) tend to look more balanced in a grid layout than mixing very different shapes — a mix of tall and wide photos in the same grid can leave some cells looking cropped or oddly spaced, so sorting or cropping photos to a closer match beforehand usually gives a cleaner result.`,
};

export default function CollageMaker() {
  const [images, setImages] = useState<File[]>([]);
  const [layout, setLayout] = useState<string>("4 (grid)");
  const [spacing, setSpacing] = useState(8);
  const [bgColor, setBgColor] = useState("#ffffff");
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const { logUsageNow } = useLogUsageOnAction();

  const handleFiles = useCallback((files: File[]) => {
    setImages(prev => {
      const all = [...prev, ...files].slice(0, 9);
      if (prev.length + files.length > 9) toast.warning(`A collage holds at most 9 photos - ${prev.length + files.length - 9} extra photo(s) were not added.`);
      return all;
    });
    setResultUrl(null); setResultBlob(null);
  }, []);
  useEffect(() => {
    // Pick a layout that uses every photo the first time they are added.
    const n = images.length;
    const best: Record<number, string> = { 2: "2 (side by side)", 3: "3 (1 big + 2)", 4: "4 (grid)", 5: "5 (2 + 3)", 6: "6 (grid)", 7: "9 (grid)", 8: "9 (grid)", 9: "9 (grid)" };
    if (LAYOUTS[layout].cells.length !== n && best[n]) setLayout(best[n]);
  }, [images.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const [cellSize, setCellSize] = useState(500);
  const [asJpg, setAsJpg] = useState(false);
  const [fitWhole, setFitWhole] = useState(false);
  const clearResult = () => { setResultUrl(null); setResultBlob(null); };
  const move = (i: number, d: number) => { setImages(a => { const b = [...a]; const j = i + d; if (j < 0 || j >= b.length) return a; [b[i], b[j]] = [b[j], b[i]]; return b; }); clearResult(); };
  const removeAt = (i: number) => { setImages(a => a.filter((_, k) => k !== i)); clearResult(); };

  const cellsNow = LAYOUTS[layout].cells.length;
  const generate = async () => {
    const cfg = LAYOUTS[layout];
    const cellCount = cfg.cells.length;
    const usable = images.slice(0, cellCount);
    if (usable.length === 0) return;
    // Logged here -- the actual generate action -- not on each file
    // drop (same fix as the other multi-file tools tonight): re-
    // selecting or adjusting the photo set before generating no longer
    // burns extra free uses for what's still just one unfinished
    // collage.
    const allowed = await logUsageNow();
    if (!allowed) return;

    let loaded: HTMLCanvasElement[];
    try {
      loaded = (await Promise.all(usable.map(f => decodeImage(f)))).map(d => d.canvas);
    } catch (e) { toast.error((e as Error)?.message || "A photo could not be opened"); return; }

    const canvas = document.createElement("canvas");
    canvas.width = cfg.cols * cellSize + (cfg.cols + 1) * spacing;
    canvas.height = cfg.rows * cellSize + (cfg.rows + 1) * spacing;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    loaded.forEach((img, i) => {
      const [cx, cy, cw, ch] = cfg.cells[i];
      const x = spacing + cx * (cellSize + spacing);
      const y = spacing + cy * (cellSize + spacing);
      const w = cw * cellSize + (cw - 1) * spacing;
      const h = ch * cellSize + (ch - 1) * spacing;
      const p = placeInCell(img.width, img.height, x, y, w, h, fitWhole);
      ctx.drawImage(img, p.sx, p.sy, p.sw, p.sh, p.dx, p.dy, p.dw, p.dh);
    });

    const blob = await canvasToBlob(canvas, asJpg ? "image/jpeg" : "image/png", 0.92);
    setResultBlob(blob);
    setResultUrl(URL.createObjectURL(blob));
    // Short and at the top so it never covers the Layout buttons on a phone.
    toast.success(`Collage ready (${canvas.width} x ${canvas.height})`, { position: "top-center", duration: 2500 });
  };

  const download = () => {
    if (!resultBlob) return;
    downloadBlob(resultBlob, asJpg ? "collage.jpg" : "collage.png");
  };

  return (
    <ToolContainer>
      {images.length === 0 ? (
        <FileDropZone
          accept="image/*"
          multiple
          onFiles={handleFiles}
          label="Drop 2-9 photos to combine"
          description="Choose a layout, then generate your collage"
          deferUsageLog
        />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
            <p className="text-sm">
              {images.length} photo{images.length === 1 ? "" : "s"} loaded
            </p>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setImages([]);
                clearResult();
              }}
            >
              Change photos
            </Button>
          </div>

          <ul className="space-y-1" data-testid="collage-photos">
            {images.map((f, i) => {
              const cfg = LAYOUTS[layout];
              const cells = cfg.cells.length;
              const used = i < cells;
              return (
                <li key={f.name + i} className={`flex items-center gap-2 text-xs rounded border px-2 py-1 ${used ? "" : "opacity-50"}`}>
                  <span className="w-5 text-muted-foreground">{i + 1}</span>
                  <span className="flex-1 truncate">{f.name}</span>
                  {!used && <span className="text-amber-600">not used in this layout</span>}
                  <button aria-label="Move up" onClick={() => move(i, -1)}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button aria-label="Move down" onClick={() => move(i, 1)}><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button aria-label="Remove" onClick={() => removeAt(i)}><X className="h-3.5 w-3.5" /></button>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap gap-4 items-center text-sm">
            <label>Size per photo:{" "}
              <select className="border rounded px-1 bg-background" value={cellSize} onChange={e => { setCellSize(Number(e.target.value)); clearResult(); }}>
                <option value={500}>500 px</option><option value={800}>800 px</option><option value={1200}>1200 px</option>
              </select>
            </label>
            <label>Format:{" "}
              <select className="border rounded px-1 bg-background" value={asJpg ? "jpg" : "png"} onChange={e => { setAsJpg(e.target.value === "jpg"); clearResult(); }}>
                <option value="png">PNG</option><option value="jpg">JPG (smaller)</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={fitWhole} onChange={e => { setFitWhole(e.target.checked); clearResult(); }} />
              Fit (no crop)
            </label>
          </div>
          <div>
            <Label className="mb-2 block text-sm">Layout</Label>
            <div className="flex flex-wrap gap-2">
              {Object.keys(LAYOUTS).map(l => (
                <Button
                  key={l}
                  size="sm"
                  variant={layout === l ? "default" : "outline"}
                  onClick={() => { setLayout(l); clearResult(); }}
                >
                  {l}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Label className="text-sm shrink-0">Spacing</Label>
            <input
              type="range"
              min={0}
              max={30}
              value={spacing}
              onChange={e => { setSpacing(Number(e.target.value)); clearResult(); }}
              className="w-full"
            />
          </div>

          <div className="flex items-center gap-3 text-sm">
            <Label className="shrink-0">Background</Label>
            <input type="color" value={bgColor} onChange={e => { setBgColor(e.target.value); clearResult(); }} className="h-8 w-8 rounded border" />
          </div>
          {images.length > cellsNow && (
            <NoticeBox className="text-xs">This layout shows {cellsNow} of your {images.length} photos. Pick a layout with more cells to use them all.</NoticeBox>
          )}

          <Button onClick={generate}>Generate Collage</Button>

          {resultUrl && (
            <div className="space-y-3">
              <img
                src={resultUrl}
                alt="Collage"
                className="w-full rounded-lg border"
              />
              <DownloadButton onClick={download} markHint={asJpg ? "collage.jpg" : "collage.png"} label="Download Collage" />
            </div>
          )}
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
